/**
 * The compose PLAN — the first step of the one Create route (it replaces the
 * maintained-fit recommendation). Given the creator's idea, the composed
 * template catalog and the module library, the planner says how much of the
 * game already exists:
 *   - `template`: a template plays like the idea; only names, words, numbers
 *     and colours change;
 *   - `adapt`: a template plus structural changes built from library modules
 *     (`changes`, in the creator's terms);
 *   - `extend`: a template plus structural changes plus rules the library
 *     lacks (`missing`, each generated, proved and then shared);
 *   - `generate`: no template is close; the game is composed from library
 *     modules plus any `missing` rules, and becomes a new template;
 *   - `none`: nothing a module could express (a dialogue story): an honest
 *     "not yet supported", never a whole-game fallback.
 * ADVISORY like the fit it replaces: the creator reviews and acknowledges,
 * and every step after it is validated and proved.
 */
import { arrayCountIssue, textLengthIssue, throwSchemaIssues, unknownFieldIssues, type SchemaIssueV1 } from '@djgg/core';
import { conversionCoverageIssuesV1, parseConversionCoverageV1, type ConversionCoverageV1 } from './composedConversion.js';
import type { ModuleRole } from '@djgg/gameplay-kit/compose';
import { fitArtIssuesV1, fitItemsIssuesV1, fitItemsV1, parseFitArtV1, MAINTAINED_FIT_MAX_ITEMS, MAINTAINED_FIT_EXPLANATION_MAX_LEN, type MaintainedFitCandidateV1 } from './maintainedGameFit.js';
import type { CompositionArtDirectionV1 } from './spec/gameSpec.js';
import { composeRuleRuntimeIssuesV1, parseComposeRuleRuntimeV1, type ComposeRuleRuntimeV1 } from './composeRuntime.js';

export const COMPOSE_PLAN_FITS = ['template', 'adapt', 'extend', 'generate', 'none'] as const;
export type ComposePlanFitV1 = typeof COMPOSE_PLAN_FITS[number];
/** `missing` caps the rules ONE plan generates (owner decision 2026-10-30: every
 * game that went live generated at most one rule; every 3–4 rule assembly
 * failed). A game grows past it through Iterate. Saved plans are never
 * re-parsed against it; COMPOSED_GENERATED_MODULES_MAX still bounds a saved run. */
export const COMPOSE_PLAN_LIMITS = Object.freeze({ missing: 2, need: 400, sketch: 600, included: 16 });
/** Cumulative recorded-spend limit before another composed generation stage.
 * Includes failed attempts on this draft; a stage already in flight can finish
 * above the limit. Build/media generation has its own bounded allowance. */
export const COMPOSED_CREATE_BUDGET_USD = 8;
/** A fresh Create or an Iterate change starts no new attempt at the creator's idea
 * (a rule, template or adaptation) that would take the run's spend past this, by
 * the estimate below. A conversion is not held to it (owner decision 196).
 * Spend counts from the run's start, like the budget above, so analysis isn't in it. */
export const COMPOSED_IDEA_BUDGET_USD = 5.5;
/** What one attempt is expected to cost. Recorded attempts ran $1.25 to $1.84. */
export const COMPOSED_ATTEMPT_ESTIMATE_USD = 1.3;
/** A fresh Create or a change starts no new attempt at the idea this long after its run started. */
export const COMPOSED_IDEA_TIME_LIMIT_MS = 30 * 60_000;
/** Wall bound of one attempt at the idea in a fresh Create or a change, far under a stage's
 * worst case, so a slow rule can't outlast the limit above by much. The longest
 * recent recorded attempt took about 10 minutes. */
export const COMPOSED_ATTEMPT_MAX_MS = 15 * 60_000;
/** A conversion's extra cumulative spend per explicit reclaim of a failed or expired
 * paid stage; a change has none, it is held to the idea's limits instead. The stage's own request/call/token bounds still bound that retry. */
export const COMPOSED_PAID_RETRY_ALLOWANCE_USD = 2;
/** Reclaims a creator may make on one run, so a conversion's ceiling stops at $12. Superadmin runs are not limited. */
export const COMPOSED_CREATOR_RETRY_LIMIT = 2;
const MODULE_ROLES: readonly ModuleRole[] = ['movement', 'behaviour', 'rule', 'world', 'flow', 'presentation'];
/** A role-namespaced module id (`rule.dig`, `flow.escalation`): the grammar
 * every library and generated module id follows. */
export const MODULE_ID_PATTERN = '[a-z]+\\.[a-zA-Z][a-zA-Z0-9]*';
const MODULE_ID = new RegExp(`^${MODULE_ID_PATTERN}$`);

/** One rule the library lacks, as the planner names it (module generation's input). */
export interface ComposeModuleNeedV1 {
  /** Role-namespaced module id, not in the library (`rule.dig`). */
  id: string;
  attach: 'entity' | 'global';
  role: ModuleRole;
  /** What the rule must do. */
  need: string;
  /** How it should work (states, signals, numbers). */
  sketch: string;
  runtime?: ComposeRuleRuntimeV1;
}

export interface ComposePlanV1 {
  schemaVersion: 1;
  fit: ComposePlanFitV1;
  /** The base template's selection id (`composed@1/<template>`) for
   * template/adapt/extend; null for generate/none. */
  templateId: string | null;
  included: string[];
  notIncluded: string[];
  /** Structural changes against the template, in the creator's terms. */
  changes: string[];
  missing: ComposeModuleNeedV1[];
  explanation: string;
  /** The idea's look on the template's art slots (template-based fits);
   * null also when a revision keeps its live game
   * ({@link composePlanKeepsTemplateV1}), whose art carries over. */
  art: CompositionArtDirectionV1 | null;
  /** Mandatory traceability for mechanics-preserving legacy conversions. */
  preservation?: ConversionCoverageV1[];
}

const STOCK_PLAN_ERRORS = {
  fit: 'stock game: fit must be "template" with its templateId',
  changes: 'stock game: changes must be empty',
  missing: 'stock game: missing must be empty',
  notIncluded: 'stock game: notIncluded must name what the game leaves out of the idea',
} as const;
/** Closed text, so a repair turn may show it to the model verbatim. */
export const COMPOSE_STOCK_PLAN_ERRORS_V1: readonly string[] = Object.values(STOCK_PLAN_ERRORS);

/** Why a plan isn't a stock-game rebuild: an unchanged catalog template, naming
 * what it leaves out so the creator can be told. */
export function composeStockPlanErrorsV1(plan: ComposePlanV1): string[] {
  return [
    ...(plan.fit === 'template' && plan.templateId ? [] : [STOCK_PLAN_ERRORS.fit]),
    ...(plan.changes.length ? [STOCK_PLAN_ERRORS.changes] : []),
    ...(plan.missing.length ? [STOCK_PLAN_ERRORS.missing] : []),
    ...(plan.notIncluded.length ? [] : [STOCK_PLAN_ERRORS.notIncluded]),
  ];
}

/** The last card's line for a game that fell back to a stock game. */
export function composedNotInYetLineV1(notInYet: readonly string[]): string {
  return `Not in yet: ${notInYet.join('; ')}. Ask for ${notInYet.length === 1 ? 'it' : 'them'} with Change.`;
}

/** Every defect of one missing rule at `at`, in parse order. */
function composeModuleNeedIssuesV1(value: unknown, moduleIds: ReadonlySet<string>, at: string): SchemaIssueV1[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [{ path: at, constraint: 'must be an object {id, attach, role, need, sketch, runtime?}' }];
  const raw = value as Record<string, unknown>;
  const issues = unknownFieldIssues(raw, ['id', 'attach', 'role', 'need', 'sketch', 'runtime'], at);
  if (typeof raw.id !== 'string' || !MODULE_ID.test(raw.id) || raw.id.length > 64) issues.push({ path: `${at}.id`, constraint: 'must be a module id like rule.dig', limit: 64, unit: 'chars' });
  else if (moduleIds.has(raw.id)) issues.push({ path: `${at}.id`, constraint: `${raw.id} already exists in the library — use it instead of generating it` });
  if (raw.attach !== 'entity' && raw.attach !== 'global') issues.push({ path: `${at}.attach`, constraint: 'must be entity or global' });
  if (!MODULE_ROLES.includes(raw.role as ModuleRole)) issues.push({ path: `${at}.role`, constraint: `must be one of ${MODULE_ROLES.join(', ')}` });
  if (raw.role === 'movement' && raw.attach !== 'entity') issues.push({ path: `${at}.attach`, constraint: 'a movement module attaches to an entity' });
  if (raw.runtime !== undefined) {
    const runtimeIssues = composeRuleRuntimeIssuesV1(raw.runtime, `${at}.runtime`);
    issues.push(...runtimeIssues);
    const runtime = runtimeIssues.length ? null : raw.runtime as { input: string; hooks?: string[] };
    const steers = !!runtime?.hooks?.includes('steer');
    if (runtime && (runtime.input === 'player' || steers) && (raw.role !== 'movement' || raw.attach !== 'entity')) {
      issues.push({ path: `${at}.runtime`, constraint: 'player input/steer belongs only to entity movement, never an always-running behaviour' });
    }
    if (steers && runtime?.input !== 'player') issues.push({ path: `${at}.runtime.input`, constraint: 'steer must belong to the declared player movement owner' });
  }
  for (const field of ['need', 'sketch'] as const) {
    const issue = textLengthIssue(`${at}.${field}`, raw[field], COMPOSE_PLAN_LIMITS[field]);
    if (issue) issues.push(issue);
  }
  return issues;
}

/** Closed parser of one missing rule (`at` names it in errors). */
export function parseComposeModuleNeedV1(value: unknown, moduleIds: ReadonlySet<string>, at = 'need'): ComposeModuleNeedV1 {
  throwSchemaIssues(composeModuleNeedIssuesV1(value, moduleIds, at));
  const raw = value as Record<string, unknown>;
  return { id: raw.id as string, attach: raw.attach as ComposeModuleNeedV1['attach'], role: raw.role as ModuleRole,
    need: (raw.need as string).trim(), sketch: (raw.sketch as string).trim(),
    ...(raw.runtime !== undefined ? { runtime: parseComposeRuleRuntimeV1(raw.runtime, `${at}.runtime`) } : {}) };
}

/** Every defect of a plan's `included` lines; a plan that builds a game lists at least one. */
function composePlanIncludedIssuesV1(value: unknown, buildsGame: boolean): SchemaIssueV1[] {
  const issues = fitItemsIssuesV1(value, 'included', COMPOSE_PLAN_LIMITS.included);
  if (!issues.length && buildsGame && !(value as unknown[]).length) {
    return [{ path: 'included', constraint: 'a plan that builds a game lists what it includes', limit: `1..${COMPOSE_PLAN_LIMITS.included}`, unit: 'items' }];
  }
  return issues;
}

/** The plan's lines of what a game it builds includes, in the creator's
 * terms (also the generate step's input: what the composed game must play). */
export function parseComposePlanIncludedV1(value: unknown): string[] {
  throwSchemaIssues(composePlanIncludedIssuesV1(value, true));
  return fitItemsV1(value, 'included', COMPOSE_PLAN_LIMITS.included);
}

/** A template a plan may name, with its sprite slots keyed by the template's kinds. */
export type ComposePlanTemplateV1 = Pick<MaintainedFitCandidateV1, 'templateId' | 'mediaSlots'>;

/** What a plan is checked against: the catalog candidates, the module ids,
 * and — for a REVISION of a composed game — the live game's template, which
 * the plan may keep even when it is the creation's own generated template
 * (never a catalog candidate). Null for a fresh Create and a conversion. */
export type ComposePlanContextV1 = {
  candidates: ReadonlyArray<ComposePlanTemplateV1>;
  moduleIds: ReadonlySet<string>;
  live: ComposePlanTemplateV1 | null;
};

/** Whether a plan keeps a revision's live template as its base game: a
 * "template" or an "extend" with no structural changes on that template
 * (the revision's base route). Such a plan may leave "art" null: the live
 * game's art direction carries over wherever its subjects are unchanged. */
export function composePlanKeepsTemplateV1(plan: { fit: ComposePlanFitV1; templateId: unknown; changes: readonly unknown[] }, liveTemplateId: string): boolean {
  return (plan.fit === 'template' || plan.fit === 'extend') && plan.changes.length === 0 && plan.templateId === liveTemplateId;
}

/** The one closed-schema walk: EVERY defect of the plan, each at its JSON path,
 * in one list (a sub-object's issues included, never just its first). */
export function composePlanSchemaIssuesV1(input: unknown, context: ComposePlanContextV1): SchemaIssueV1[] {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return [{ path: '$', constraint: 'the plan must be one JSON object' }];
  const raw = input as Record<string, unknown>;
  const issues = unknownFieldIssues(raw, ['schemaVersion', 'fit', 'templateId', 'included', 'notIncluded', 'changes', 'missing', 'explanation', 'art', 'preservation'], '$');
  if (raw.schemaVersion !== undefined && raw.schemaVersion !== 1) issues.push({ path: 'schemaVersion', constraint: 'must be 1' });
  const fit = raw.fit as ComposePlanFitV1;
  const knownFit = COMPOSE_PLAN_FITS.includes(fit);
  if (!knownFit) issues.push({ path: 'fit', constraint: `must be one of ${COMPOSE_PLAN_FITS.join(', ')}` });
  const templateId = raw.templateId ?? null;
  const based = fit === 'template' || fit === 'adapt' || fit === 'extend';
  const live = context.live;
  const named = live && !context.candidates.some(c => c.templateId === live.templateId) ? [...context.candidates, live] : context.candidates;
  const candidate = based ? named.find(c => c.templateId === templateId) : undefined;
  if (based && !candidate) issues.push({ path: 'templateId', constraint: `fit "${fit}" needs one of: ${named.map(c => c.templateId).join(', ')}` });
  if (knownFit && !based && templateId !== null) issues.push({ path: 'templateId', constraint: `must be null for fit "${fit}"` });
  const rawChanges = raw.changes ?? [];
  issues.push(...fitItemsIssuesV1(rawChanges, 'changes', MAINTAINED_FIT_MAX_ITEMS));
  const rawMissing = raw.missing ?? [];
  const missingCount = arrayCountIssue('missing', rawMissing, COMPOSE_PLAN_LIMITS.missing);
  if (missingCount) issues.push({ ...missingCount, constraint: missingCount.actual === undefined ? missingCount.constraint : 'too many rules to generate in one plan' });
  const missingItems: unknown[] = Array.isArray(rawMissing) ? rawMissing : [];
  missingItems.forEach((m, i) => issues.push(...composeModuleNeedIssuesV1(m, context.moduleIds, `missing[${i}]`)));
  const missingIds = missingItems.map(m => (m as { id?: unknown } | null)?.id);
  missingIds.forEach((id, i) => {
    const first = missingIds.indexOf(id);
    if (typeof id === 'string' && first < i) issues.push({ path: `missing[${i}].id`, constraint: `duplicates missing[${first}].id; missing ids must be unique` });
  });
  const hasChanges = !Array.isArray(rawChanges) || rawChanges.length > 0;
  const hasMissing = !Array.isArray(rawMissing) || rawMissing.length > 0;
  if (fit === 'template' && (hasChanges || hasMissing)) issues.push({ path: 'fit', constraint: '"template" must leave "changes" and "missing" empty; say its data tuning in "included", or choose "adapt" or "extend"' });
  if (fit === 'adapt' && (!hasChanges || hasMissing)) issues.push({ path: 'fit', constraint: '"adapt" lists its changes and generates nothing' });
  if (fit === 'extend' && !hasMissing) issues.push({ path: 'fit', constraint: '"extend" lists the missing rules it generates' });
  if (fit === 'none' && (hasChanges || hasMissing)) issues.push({ path: 'fit', constraint: '"none" changes and generates nothing' });
  const explanation = textLengthIssue('explanation', raw.explanation, MAINTAINED_FIT_EXPLANATION_MAX_LEN);
  if (explanation) issues.push(explanation);
  issues.push(...composePlanIncludedIssuesV1(raw.included, fit !== 'none'));
  const resolved = knownFit && (!based || !!candidate);
  if (resolved && !candidate?.mediaSlots.length && raw.art != null) issues.push({ path: 'art', constraint: 'must be null: art is mapped only onto a template\'s art slots (template/adapt/extend)' });
  issues.push(...fitItemsIssuesV1(raw.notIncluded ?? [], 'notIncluded', MAINTAINED_FIT_MAX_ITEMS));
  if (raw.preservation !== undefined) issues.push(...conversionCoverageIssuesV1(raw.preservation));
  if (candidate?.mediaSlots.length && !composePlanKeepsLiveArt(raw, context)) issues.push(...fitArtIssuesV1(raw.art, candidate.mediaSlots.map(o => o.slot)));
  return issues;
}

/** A revision plan keeping the live template may leave "art" null: its art carries over. */
function composePlanKeepsLiveArt(raw: Record<string, unknown>, context: ComposePlanContextV1): boolean {
  const changes = raw.changes ?? [];
  return !!context.live && raw.art == null && composePlanKeepsTemplateV1({ fit: raw.fit as ComposePlanFitV1, templateId: raw.templateId ?? null,
    changes: Array.isArray(changes) ? changes : [changes] }, context.live.templateId);
}

/** Closed parser shared by the builder (model reply) and functions (wire). */
export function parseComposePlanV1(input: unknown, context: ComposePlanContextV1): ComposePlanV1 {
  throwSchemaIssues(composePlanSchemaIssuesV1(input, context));
  const raw = input as Record<string, unknown>;
  const fit = raw.fit as ComposePlanFitV1;
  const templateId = (raw.templateId ?? null) as string | null;
  const candidate = fit === 'template' || fit === 'adapt' || fit === 'extend'
    ? [...context.candidates, ...(context.live ? [context.live] : [])].find(c => c.templateId === templateId) : undefined;
  return {
    schemaVersion: 1, fit, templateId, included: fitItemsV1(raw.included, 'included', COMPOSE_PLAN_LIMITS.included),
    notIncluded: fitItemsV1(raw.notIncluded ?? [], 'notIncluded', MAINTAINED_FIT_MAX_ITEMS),
    changes: fitItemsV1(raw.changes ?? [], 'changes', MAINTAINED_FIT_MAX_ITEMS),
    missing: ((raw.missing ?? []) as unknown[]).map((m, i) => parseComposeModuleNeedV1(m, context.moduleIds, `missing[${i}]`)),
    explanation: (raw.explanation as string).trim(),
    ...(raw.preservation !== undefined ? { preservation: parseConversionCoverageV1(raw.preservation) } : {}),
    art: candidate?.mediaSlots.length && !composePlanKeepsLiveArt(raw, context) ? parseFitArtV1(raw.art, candidate.mediaSlots.map(o => o.slot)) : null,
  };
}

